/*
 * AONSK V11.48 search-intent and destination router
 *
 * Preserves V11.47 strict title-field filtering for mutually exclusive
 * product types, then adds exact destination routing for customer queries
 * that clearly name an existing collection or seasonal landing page.
 *
 * Exact destination routes are intentionally narrow. Longer or cultivar-
 * specific searches continue through Shopify's normal search experience.
 */
(() => {
  if (window.__aonskSearchIntentRouterV1148) return;
  window.__aonskSearchIntentRouterV1148 = true;

  const normalize = (value) => String(value || '')
    .toLowerCase()
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[-_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const strictMappings = new Map();
  const destinationMappings = new Map();

  const addStrict = (queries, strictQuery) => {
    queries.forEach((query) => strictMappings.set(normalize(query), strictQuery));
  };

  const addDestination = (queries, destination) => {
    queries.forEach((query) => destinationMappings.set(normalize(query), destination));
  };

  // High-volume navigation queries with strong live collection destinations.
  addDestination([
    'fall garden', 'fall gardens', 'fall garden seeds', 'fall planting',
    'fall seeds', 'autumn garden', 'autumn gardens', 'autumn planting',
    'autumn seeds', 'seeds for fall', 'fall vegetable seeds'
  ], '/collections/fall-garden');

  addDestination([
    'tea garden', 'tea gardens', 'tea garden seeds', 'tea seeds',
    'herbal tea seeds', 'herb tea seeds'
  ], '/collections/tea-garden');

  addDestination([
    'pollinator garden', 'pollinator gardens', 'pollinator seeds',
    'pollinator flower seeds', 'butterfly garden', 'butterfly gardens'
  ], '/collections/pollinator-patch');

  addDestination([
    'seed starting', 'seed starting supplies', 'seed starter supplies',
    'seed starting equipment', 'starting seeds', 'seed trays',
    'seedling trays', 'grow lights'
  ], '/collections/seed-starting-supplies');

  addDestination([
    'seed potato', 'seed potatoes', 'potato seed', 'potato seeds',
    'potato', 'potatoes'
  ], '/collections/seed-potatoes');

  addDestination([
    'garlic', 'garlic seed', 'garlic seeds', 'seed garlic',
    'garlic bulb', 'garlic bulbs'
  ], '/collections/garlic-bulbs');

  addDestination([
    'strawberry', 'strawberries', 'strawberry seed', 'strawberry seeds'
  ], '/collections/strawberry-seeds');

  addDestination([
    'heirloom seed', 'heirloom seeds', 'heritage seed', 'heritage seeds'
  ], '/collections/all-heirloom-seeds');

  addDestination([
    'vegetable seed', 'vegetable seeds', 'garden vegetable seeds'
  ], '/collections/all-vegetable-seeds');

  addDestination(['flower seed', 'flower seeds'], '/collections/all-flower-seeds');
  addDestination(['herb seed', 'herb seeds'], '/collections/all-herb-seeds');
  addDestination([
    'cover crop', 'cover crops', 'cover crop seed', 'cover crop seeds'
  ], '/collections/cover-crop-seeds');
  addDestination(['native seed', 'native seeds'], '/collections/native-seeds');

  // V11.47 strict product-type mappings retained unchanged.
  addStrict([
    'determinate tomato', 'determinate tomatoes', 'determinate tomato seed',
    'determinate tomato seeds', 'tomato determinate', 'tomatoes determinate',
    'tomato seed determinate', 'tomato seeds determinate', 'bush tomato',
    'bush tomatoes', 'bush tomato seed', 'bush tomato seeds'
  ], 'title:tomato title:determinate');

  addStrict([
    'indeterminate tomato', 'indeterminate tomatoes',
    'indeterminate tomato seed', 'indeterminate tomato seeds',
    'tomato indeterminate', 'tomatoes indeterminate',
    'tomato seed indeterminate', 'tomato seeds indeterminate',
    'vining tomato', 'vining tomatoes', 'vining tomato seed',
    'vining tomato seeds'
  ], 'title:tomato title:indeterminate');

  addStrict([
    'edamame', 'edamame seed', 'edamame seeds', 'soybean', 'soybeans',
    'soybean seed', 'soybean seeds', 'soy bean', 'soy beans',
    'soy bean seed', 'soy bean seeds'
  ], 'title:edamame');

  addStrict([
    'cowpea', 'cowpeas', 'cowpea seed', 'cowpea seeds', 'southern pea',
    'southern peas', 'southern pea seed', 'southern pea seeds',
    'black eyed pea', 'black eyed peas', 'black eyed pea seed',
    'black eyed pea seeds', 'black-eye pea', 'black-eye peas',
    'black-eyed pea', 'black-eyed peas'
  ], 'title:cowpeas');

  addStrict([
    'scallion', 'scallions', 'scallion seed', 'scallion seeds',
    'green onion', 'green onions', 'green onion seed', 'green onion seeds',
    'bunching onion', 'bunching onions', 'bunching onion seed',
    'bunching onion seeds', 'spring onion', 'spring onions'
  ], 'title:bunching title:onion');

  addStrict([
    'luffa', 'luffa seed', 'luffa seeds', 'loofah', 'loofah seed',
    'loofah seeds', 'loofa', 'loofa seed', 'loofa seeds', 'sponge gourd',
    'sponge gourd seed', 'sponge gourd seeds'
  ], 'title:luffa');

  const redirectCurrentSearchIfNeeded = () => {
    const path = window.location.pathname.replace(/\/+$/, '') || '/';
    if (path !== '/search') return false;

    const url = new URL(window.location.href);
    const destination = destinationMappings.get(normalize(url.searchParams.get('q')));
    if (destination) {
      window.location.replace(destination);
      return true;
    }
    const strictQuery = strictMappings.get(normalize(url.searchParams.get('q')));
    const needsStockOrder = !url.searchParams.has('options[unavailable_products]');
    if (!strictQuery && !needsStockOrder) return false;
    if (strictQuery) url.searchParams.set('q', strictQuery);
    if (needsStockOrder) url.searchParams.set('options[unavailable_products]', 'last');
    if (strictQuery) url.searchParams.set('type', 'product');
    if (strictQuery) url.searchParams.delete('page');
    window.location.replace(url.toString());
    return true;
  };

  addStrict([
    'dwarf sunflower', 'dwarf sunflowers', 'dwarf sunflower seed', 'dwarf sunflower seeds',
    'sunflower dwarf', 'sunflowers dwarf', 'sunflower seeds dwarf'
  ], 'title:sunflower AND title:dwarf AND product_type:"Flower Seeds"');
  addStrict([
    'ornamental sunflower', 'ornamental sunflowers', 'ornamental sunflower seeds',
    'sunflower', 'sunflowers', 'sunflower seed', 'sunflower seeds',
    'sunflwer', 'sunflwer seeds', 'sunfower', 'sunfower seeds'
  ], 'title:sunflower AND product_type:"Flower Seeds"');
  addStrict(['gourd', 'gourds', 'gourd seed', 'gourd seeds'], 'title:gourd');
  // Culinary varieties verified against the current catalog; do not include ornamental gourds.
  addStrict(['edible gourd', 'edible gourds', 'edible gourd seed', 'edible gourd seeds',
    'culinary gourd', 'culinary gourds', 'culinary gourd seeds'], 'title:gourd AND (title:cucuzzi OR title:luffa OR (title:nam AND title:tao))');
  addStrict(['bottle gourd', 'bottle gourds', 'bottle gourd seed', 'bottle gourd seeds'],
    'title:gourd AND title:bottle');
  addStrict(['black oil sunflower', 'black oil sunflower seed', 'black oil sunflower seeds'],
    'title:sunflower AND title:black AND title:oil AND product_type:"Flower Seeds"');
  addStrict(['lemon queen sunflower', 'lemon queen sunflowers', 'lemon queen sunflower seeds'],
    'title:sunflower AND title:lemon AND title:queen AND product_type:"Flower Seeds"');
  addDestination(['zucchini', 'zucchini seed', 'zucchini seeds', 'zuchini', 'zuchini seeds',
    'zuccini', 'zuccini seeds'], '/collections/zucchini-seeds');

  addStrict(['spaghetti squash', 'spaghetti squash seeds', 'spaghetti squash seed'],
    'title:squash AND title:spaghetti');
  addStrict(['summer squash', 'summer squash seeds', 'summer squash seed'],
    '((title:squash AND title:summer) OR title:zucchini) AND product_type:"Vegetable Seeds"');
  addStrict(['container tomato', 'container tomatoes', 'container tomato seeds',
    'patio tomato', 'patio tomatoes', 'patio tomato seeds'],
    'title:tomato AND (tag:"Dwarf Tomato Seeds" OR tag:"Micro Tomato Seeds")');
  addStrict(['dwarf patio tomato', 'dwarf patio tomatoes', 'dwarf patio tomato seeds',
    'dwarf tomato', 'dwarf tomatoes', 'dwarf tomato seeds'],
    'title:tomato AND (tag:"Dwarf Tomato Seeds" OR tag:"Micro Tomato Seeds")');

  addStrict(['black magic kale', 'black magic kale seeds', 'black magic kale seed'],
    'title:kale AND title:black AND title:magic');

  addStrict(['cabbage', 'cabbages', 'cabbage seed', 'cabbage seeds', 'heading cabbage', 'heading cabbage seeds'],
    'title:cabbage AND -title:collard AND -title:kale AND -title:chinese AND -title:pak AND -title:bok AND -title:tatsoi AND product_type:"Vegetable Seeds"');

  addStrict(['basil', 'basil seed', 'basil seeds'], 'title:basil AND product_type:"Herb Seeds"');
  window.aonskSearchQuery = (value) => strictMappings.get(normalize(value)) || value;

  if (redirectCurrentSearchIfNeeded()) return;

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('form[role="search"], form.search')) return;

    const input = form.querySelector('input[name="q"]');
    if (!input) return;

    const normalizedQuery = normalize(input.value);
    const destination = destinationMappings.get(normalizedQuery);
    if (destination) {
      event.preventDefault();
      window.location.assign(destination);
      return;
    }

    const strictQuery = strictMappings.get(normalizedQuery);
    if (!strictQuery) return;

    if (form.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')) return;
    event.preventDefault();
    const searchUrl = new URL(form.action || '/search', window.location.origin);
    const searchParams = new URLSearchParams(new FormData(form));
    searchParams.set('q', strictQuery);
    searchParams.set('type', 'product');
    searchParams.set('options[unavailable_products]', 'last');
    searchUrl.search = searchParams.toString();
    window.location.assign(searchUrl.toString());
  }, true);
})();



